import { Box, Chip, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import { ArchieChatMessage as ArchieChatMessageType } from "../../types/archie/archie";

interface Props {
  message: ArchieChatMessageType;
}

/**
 * Strips the raw search URL and surrounding "click this link:" boilerplate
 * from assistant text, since the "View Results" chip handles navigation.
 */
const cleanAssistantText = (text: string, searchUrl?: string): string => {
  if (!searchUrl) return text;

  let cleaned = text;

  // Remove the raw URL itself (may be URL-encoded or plain)
  cleaned = cleaned.replace(searchUrl, "");

  // Remove common boilerplate phrases that wrap the URL
  cleaned = cleaned.replaceAll(
    /You can view (?:these|the) interactions by clicking (?:on )?(?:the following|this) link:\s*/gi,
    ""
  );
  cleaned = cleaned.replaceAll(/by clicking (?:on )?(?:the following|this) link:\s*/gi, "");
  cleaned = cleaned.replaceAll(/clicking (?:the following|this) link:\s*/gi, "");
  cleaned = cleaned.replaceAll(/Here(?:'s| is) the link(?:\s*to view them)?:\s*/gi, "");

  // Clean up leftover whitespace and blank lines
  cleaned = cleaned.replaceAll(/\n{3,}/g, "\n\n").trim();

  return cleaned;
};

const ArchieChatMessage = ({ message }: Readonly<Props>) => {
  const navigate = useNavigate();
  const isUser = message.role === "user";
  const displayText = isUser
    ? message.text
    : cleanAssistantText(message.text, message.searchUrl);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isUser ? "flex-start" : "flex-end",
        mb: 1.5,
      }}
    >
      <Box
        sx={{
          maxWidth: "85%",
          px: 2,
          py: 1,
          borderRadius: 2,
          backgroundColor: isUser ? "grey.100" : "primary.main",
          color: isUser ? "text.primary" : "white",
        }}
      >
        <Typography
          variant="body2"
          sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
        >
          {displayText}
        </Typography>

        {message.searchUrl && (
          <Chip
            icon={<SearchIcon sx={{ color: "inherit" }} />}
            label="View Results"
            size="small"
            onClick={() => navigate(message.searchUrl!)}
            sx={{
              mt: 1,
              cursor: "pointer",
              backgroundColor: isUser ? "primary.main" : "rgba(255,255,255,0.2)",
              color: "white",
              "&:hover": {
                backgroundColor: isUser
                  ? "primary.dark"
                  : "rgba(255,255,255,0.35)",
              },
            }}
          />
        )}

        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 0.5,
            opacity: 0.7,
            color: isUser ? "text.secondary" : "rgba(255,255,255,0.7)",
          }}
        >
          {message.timestamp.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </Typography>
      </Box>
    </Box>
  );
};

export default ArchieChatMessage;
